import {
	createActionsHook,
	createStateHook,
	createStore,
	defaultRegistry,
} from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import type { ReactionSummary } from '@atlaskit/reactions';
import type { InlineCommentSelectionComponentProps } from '@atlaskit/editor-common/types';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';

export type ApplyDraftMode = InlineCommentSelectionComponentProps['applyDraftMode'];
export type RemoveDraftMode = InlineCommentSelectionComponentProps['removeDraftMode'];

/**
 * State of the currently-visible picker. `onClose` is captured at open time so
 * `closePicker` can tear down draft highlights / notify the caller without the
 * store needing to know about the selection.
 *
 * `anchorTop` and `anchorLeft` are container-relative pixel offsets computed
 * at open time (both the trigger rect and the portal container rect are
 * measured at the same instant), so the popup position is stable across
 * subsequent page scrolls.
 */
export type InlineReactionPickerState = {
	anchorTop: number;
	anchorLeft: number;
	contentType: 'blogpost' | 'page';
	/** Page (content) ID passed to the mutation as `containerId`. */
	pageId: string;
	/**
	 * Called after a successful reaction creation with the server-returned annotationId,
	 * commentId, the ProseMirror step, and the serialised original selection.
	 */
	onCreate: (
		annotationId: string,
		commentId: string,
		step: AddMarkStep | undefined,
		originalSelection: string | undefined,
	) => void;
	/** ProseMirror step identifying the annotated text range. Captured from `applyDraftMode` result. */
	step?: AddMarkStep;
	/** Serialised original selection. Captured from `applyDraftMode` result. */
	originalSelection?: string;
	onClose?: () => void;
};

/** Input accepted by `openPicker`. */
export type OpenPickerOptions = InlineReactionPickerState;

/**
 * Per-comment optimistic reaction state. Keyed by commentId in the store
 * so multiple InlineReaction components can coexist without interfering.
 */
export type CommentReactionState = {
	/** Reactions array with optimistic updates applied on top of server data. */
	optimisticReactions: ReactionSummary[];
	/** Emoji IDs that have an in-flight mutation (used to prevent double-toggles). */
	inFlightEmojiIds: Set<string>;
	/** Snapshot of reactions before each optimistic update, keyed by emojiId, for rollback. */
	rollbackMap: Map<string, ReactionSummary[]>;
	/** Last mutation error, cleared on next toggle attempt. */
	error: Error | undefined;
};

export type State = {
	/**
	 * Handler registered by the host (e.g. `InlineReactionPicker`) that knows
	 * how to compute the trigger rect, apply the draft highlight, and dispatch
	 * `openPicker`. Called by `triggerOpen` when the user clicks the React
	 * button inside the highlight toolbar.
	 */
	opener: (() => void) | null;
	picker: InlineReactionPickerState | null;
	/**
	 * Optimistic entries added immediately after a reaction creation succeeds,
	 * before the server-side query (`useActiveInlineData`) refetches.
	 * Merged with the query result in `useRendererAnnotationProviderOptions` so
	 * that `ViewComponent` renders `InlineReaction` (not `InlineCommentComponent`)
	 * for the newly created annotation.
	 */
	optimisticReactionMarkerMap: Record<string, string>;
	/**
	 * Per-comment optimistic reaction state. Each entry tracks the optimistic
	 * reactions, in-flight mutations, rollback snapshots, and errors for a
	 * single comment. Keyed by numeric comment ID.
	 */
	commentReactionState: Record<string, CommentReactionState>;
};

type RegisterOpener = (opener: (() => void) | null) => Action<State>;
type TriggerOpen = () => Action<State>;
type OpenPicker = (options: OpenPickerOptions) => Action<State>;
type ClosePicker = () => Action<State>;
type AddOptimisticReaction = (markerRef: string, commentId: string) => Action<State>;
type PruneOptimisticReactions = (confirmedMarkerRefs: readonly string[]) => Action<State>;

// Reaction state actions
type SyncServerReactions = (commentId: string, serverReactions: ReactionSummary[]) => Action<State>;
/**
 * Returns `null` if guarded (in-flight or not found), `true` if removing, `false` if adding.
 * Atomically applies optimistic update + marks in-flight.
 */
type ApplyOptimisticToggle = (
	commentId: string,
	emojiId: string,
) => Action<State, void, boolean | null>;
/**
 * Returns `false` if guarded (in-flight or already reacted), `true` if applied.
 * Atomically applies optimistic update + marks in-flight.
 */
type ApplyOptimisticAdd = (
	commentId: string,
	emojiId: string,
	templateAri: string,
	templateContainerAri: string,
) => Action<State, void, boolean>;
type OnEmojiSettled = (commentId: string, emojiId: string) => Action<State>;
type OnEmojiMutationError = (commentId: string, emojiId: string, error: Error) => Action<State>;
type CleanupComment = (commentId: string) => Action<State>;
/**
 * Apply a remote delta from a pubsub event (another user's add or remove).
 *
 * delta = +1: increment count for the emoji, or create a new entry if it
 *             doesn't exist. `reacted` is NOT set to true — it reflects the
 *             current user's reaction state, not the remote actor's.
 * delta = -1: decrement count, removing the entry when count reaches zero.
 *             No-ops when the comment has no state in the store yet.
 *
 * Does not touch `inFlightEmojiIds` or `rollbackMap` — remote events are
 * not subject to local rollback.
 */
type ApplyRemoteDelta = (
	commentId: string,
	emojiId: string,
	delta: 1 | -1,
	templateAri?: string,
	templateContainerAri?: string,
) => Action<State>;

export type Actions = {
	registerOpener: RegisterOpener;
	triggerOpen: TriggerOpen;
	openPicker: OpenPicker;
	closePicker: ClosePicker;
	addOptimisticReaction: AddOptimisticReaction;
	pruneOptimisticReactions: PruneOptimisticReactions;
	syncServerReactions: SyncServerReactions;
	applyOptimisticToggle: ApplyOptimisticToggle;
	applyOptimisticAdd: ApplyOptimisticAdd;
	onEmojiSettled: OnEmojiSettled;
	onEmojiMutationError: OnEmojiMutationError;
	cleanupComment: CleanupComment;
	applyRemoteDelta: ApplyRemoteDelta;
};

const defaultCommentState: CommentReactionState = Object.freeze({
	optimisticReactions: Object.freeze([]) as [],
	inFlightEmojiIds: Object.freeze(new Set<string>()) as Set<string>,
	rollbackMap: Object.freeze(new Map<string, ReactionSummary[]>()) as Map<
		string,
		ReactionSummary[]
	>,
	error: undefined,
});

const getCommentState = (state: State, commentId: string): CommentReactionState =>
	state.commentReactionState[commentId] ?? defaultCommentState;

const setCommentState = (
	state: State,
	commentId: string,
	patch: Partial<CommentReactionState>,
): Record<string, CommentReactionState> => ({
	...state.commentReactionState,
	[commentId]: { ...getCommentState(state, commentId), ...patch },
});

/**
 * Merges server reaction data into the existing display order.
 * Existing emojis keep their position; their values are updated from `server`
 * (or from `inFlightOverrides` when a mutation is in-flight).
 * Emojis absent from both `server` and `inFlightOverrides` are dropped.
 * New emojis (from server or still-in-flight) are appended at the end.
 */
const stabilizeReactions = (
	current: ReactionSummary[],
	server: ReactionSummary[],
	inFlightOverrides: Map<string, ReactionSummary> = new Map(),
	deletedInFlight: Set<string> = new Set(),
): ReactionSummary[] => {
	const serverMap = new Map<string, ReactionSummary>();
	for (const r of server) {
		if (!deletedInFlight.has(r.emojiId)) {
			serverMap.set(r.emojiId, r);
		}
	}
	const result: ReactionSummary[] = [];
	const seenIds = new Set<string>();

	for (const r of current) {
		if (deletedInFlight.has(r.emojiId)) {
			continue;
		}
		const inFlight = inFlightOverrides.get(r.emojiId);
		if (inFlight) {
			result.push(inFlight);
			seenIds.add(r.emojiId);
		} else if (serverMap.has(r.emojiId)) {
			result.push(serverMap.get(r.emojiId)!);
			seenIds.add(r.emojiId);
		}
		// not in server and not in-flight → removed, omit
	}

	for (const r of server) {
		if (!seenIds.has(r.emojiId) && !deletedInFlight.has(r.emojiId)) {
			result.push(r);
			seenIds.add(r.emojiId);
		}
	}

	for (const [emojiId, optimistic] of inFlightOverrides) {
		if (!seenIds.has(emojiId)) {
			result.push(optimistic);
			seenIds.add(emojiId);
		}
	}

	return result;
};

const initialState: State = {
	opener: null,
	picker: null,
	optimisticReactionMarkerMap: {},
	commentReactionState: {},
};

const registerOpener: RegisterOpener =
	(opener) =>
	({ setState }) => {
		setState({ opener });
	};

const triggerOpen: TriggerOpen =
	() =>
	({ getState }) => {
		getState().opener?.();
	};

const openPicker: OpenPicker =
	({ anchorTop, anchorLeft, contentType, pageId, onCreate, step, originalSelection, onClose }) =>
	({ setState, getState }) => {
		if (getState().picker) {
			return;
		}
		setState({
			picker: {
				anchorTop,
				anchorLeft,
				contentType,
				pageId,
				onCreate,
				step,
				originalSelection,
				onClose,
			},
		});
	};

const closePicker: ClosePicker =
	() =>
	({ setState, getState }) => {
		const { picker } = getState();
		if (!picker) {
			return;
		}
		picker.onClose?.();
		setState({ picker: null });
	};

const addOptimisticReaction: AddOptimisticReaction =
	(markerRef, commentId) =>
	({ setState, getState }) => {
		setState({
			optimisticReactionMarkerMap: {
				...getState().optimisticReactionMarkerMap,
				[markerRef]: commentId,
			},
		});
	};

/**
 * Drop any optimistic entries whose markerRef is now in the server-confirmed map.
 * Called from `useRendererAnnotationProviderOptions` whenever `useActiveInlineData`
 * returns a new `reactionMarkerMap`, so the optimistic store does not grow forever.
 * No-ops (and skips the setState) when nothing needs to be pruned to avoid an
 * extra re-render cycle.
 */
const pruneOptimisticReactions: PruneOptimisticReactions =
	(confirmedMarkerRefs) =>
	({ setState, getState }) => {
		const current = getState().optimisticReactionMarkerMap;
		if (Object.keys(current).length === 0 || confirmedMarkerRefs.length === 0) {
			return;
		}
		const confirmedSet = new Set(confirmedMarkerRefs);
		let changed = false;
		const next: Record<string, string> = {};
		for (const [markerRef, commentId] of Object.entries(current)) {
			if (confirmedSet.has(markerRef)) {
				changed = true;
				continue;
			}
			next[markerRef] = commentId;
		}
		if (changed) {
			setState({ optimisticReactionMarkerMap: next });
		}
	};

/**
 * Sync server reactions into the store for a given comment.
 *
 * When nothing is in-flight, the server data replaces the optimistic array.
 * When mutations are in-flight, server data is merged: in-flight emojis keep
 * their optimistic values while everything else updates to the server truth.
 */
const syncServerReactions: SyncServerReactions =
	(commentId, serverReactions) =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);

		if (comment.inFlightEmojiIds.size === 0) {
			setState({
				commentReactionState: setCommentState(state, commentId, {
					optimisticReactions: stabilizeReactions(comment.optimisticReactions, serverReactions),
				}),
			});
			return;
		}

		// Merge: keep optimistic values for in-flight emojis, use server for the rest.
		// Build the snapshot as a Map for O(1) lookup (avoids quadratic nested iteration).
		const inFlightSnapshot = new Map<string, ReactionSummary>();
		for (const r of comment.optimisticReactions) {
			if (comment.inFlightEmojiIds.has(r.emojiId)) {
				inFlightSnapshot.set(r.emojiId, r);
			}
		}

		// Track emojis that were optimistically deleted (in-flight but absent from
		// optimisticReactions because their count hit 0). We must suppress these from
		// the server payload so they don't get resurrected before the mutation settles.
		const deletedInFlight = new Set<string>();
		for (const id of comment.inFlightEmojiIds) {
			if (!inFlightSnapshot.has(id)) {
				deletedInFlight.add(id);
			}
		}

		setState({
			commentReactionState: setCommentState(state, commentId, {
				optimisticReactions: stabilizeReactions(
					comment.optimisticReactions,
					serverReactions,
					inFlightSnapshot,
					deletedInFlight,
				),
			}),
		});
	};

/**
 * Apply an optimistic toggle (add or remove) for an existing emoji.
 * Returns `null` if the emoji is already in-flight or not found (no-op).
 * Otherwise applies the optimistic update, marks in-flight, and returns
 * `true` if removing or `false` if adding, so the caller knows which
 * mutation to fire.
 *
 * This combines the guard, optimistic update, and in-flight marking into a
 * single synchronous store transaction so that rapid-fire calls (e.g. double
 * clicks) are properly de-duped even before React re-renders.
 */
const applyOptimisticToggle: ApplyOptimisticToggle =
	(commentId, emojiId) =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);

		if (comment.inFlightEmojiIds.has(emojiId)) {
			return null;
		}

		const currentReaction = comment.optimisticReactions.find((r) => r.emojiId === emojiId);
		if (!currentReaction) {
			return null;
		}

		const isRemoving = currentReaction.reacted;

		// Save rollback snapshot
		const nextRollback = new Map(comment.rollbackMap);
		nextRollback.set(emojiId, comment.optimisticReactions);

		// Mark in-flight
		const nextInFlight = new Set(comment.inFlightEmojiIds);
		nextInFlight.add(emojiId);

		let nextReactions: ReactionSummary[];
		if (isRemoving) {
			const newCount = currentReaction.count - 1;
			if (newCount <= 0) {
				nextReactions = comment.optimisticReactions.filter((r) => r.emojiId !== emojiId);
			} else {
				nextReactions = comment.optimisticReactions.map((r) =>
					r.emojiId === emojiId ? { ...r, count: newCount, reacted: false } : r,
				);
			}
		} else {
			nextReactions = comment.optimisticReactions.map((r) =>
				r.emojiId === emojiId ? { ...r, count: r.count + 1, reacted: true } : r,
			);
		}

		setState({
			commentReactionState: setCommentState(state, commentId, {
				optimisticReactions: nextReactions,
				rollbackMap: nextRollback,
				inFlightEmojiIds: nextInFlight,
				error: undefined,
			}),
		});

		return isRemoving;
	};

/**
 * Apply an optimistic add for a new or existing-but-not-reacted emoji.
 * Returns `false` if already in-flight or already reacted (no-op).
 * Otherwise applies the optimistic update, marks in-flight, and returns `true`.
 *
 * Like `applyOptimisticToggle`, this combines guard + update + in-flight
 * into a single synchronous transaction.
 */
const applyOptimisticAdd: ApplyOptimisticAdd =
	(commentId, emojiId, templateAri, templateContainerAri) =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);

		if (comment.inFlightEmojiIds.has(emojiId)) {
			return false;
		}

		const existingReaction = comment.optimisticReactions.find((r) => r.emojiId === emojiId);
		if (existingReaction?.reacted) {
			return false;
		}

		// Save rollback snapshot
		const nextRollback = new Map(comment.rollbackMap);
		nextRollback.set(emojiId, comment.optimisticReactions);

		// Mark in-flight
		const nextInFlight = new Set(comment.inFlightEmojiIds);
		nextInFlight.add(emojiId);

		let nextReactions: ReactionSummary[];
		if (existingReaction) {
			nextReactions = comment.optimisticReactions.map((r) =>
				r.emojiId === emojiId ? { ...r, count: r.count + 1, reacted: true } : r,
			);
		} else {
			nextReactions = [
				...comment.optimisticReactions,
				{
					ari: templateAri,
					containerAri: templateContainerAri,
					emojiId,
					count: 1,
					reacted: true,
				},
			];
		}

		setState({
			commentReactionState: setCommentState(state, commentId, {
				optimisticReactions: nextReactions,
				rollbackMap: nextRollback,
				inFlightEmojiIds: nextInFlight,
				error: undefined,
			}),
		});

		return true;
	};

/** Clear in-flight and rollback state for a settled mutation. */
const onEmojiSettled: OnEmojiSettled =
	(commentId, emojiId) =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);
		const nextInFlight = new Set(comment.inFlightEmojiIds);
		nextInFlight.delete(emojiId);
		const nextRollback = new Map(comment.rollbackMap);
		nextRollback.delete(emojiId);

		setState({
			commentReactionState: setCommentState(state, commentId, {
				inFlightEmojiIds: nextInFlight,
				rollbackMap: nextRollback,
			}),
		});
	};

/** Roll back optimistic state on mutation error. */
const onEmojiMutationError: OnEmojiMutationError =
	(commentId, emojiId, error) =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);
		const rollback = comment.rollbackMap.get(emojiId);

		// Clear in-flight and rollback for this emoji
		const nextInFlight = new Set(comment.inFlightEmojiIds);
		nextInFlight.delete(emojiId);
		const nextRollback = new Map(comment.rollbackMap);
		nextRollback.delete(emojiId);

		setState({
			commentReactionState: setCommentState(state, commentId, {
				optimisticReactions: rollback ?? comment.optimisticReactions,
				inFlightEmojiIds: nextInFlight,
				rollbackMap: nextRollback,
				error,
			}),
		});
	};

/** Remove all state for a comment (call on unmount). */
const cleanupComment: CleanupComment =
	(commentId) =>
	({ setState, getState }) => {
		const state = getState();
		const { [commentId]: _, ...rest } = state.commentReactionState;
		setState({ commentReactionState: rest });
	};

/**
 * Apply a remote pubsub delta (add or remove) from another user.
 *
 * On +1: increment the existing emoji count, or create a new entry with
 *        count=1 and reacted=false if it doesn't exist yet.
 * On -1: decrement the count, removing the entry if it reaches zero.
 *        No-ops if the comment has no state yet (nothing to decrement).
 */
const applyRemoteDelta: ApplyRemoteDelta =
	(commentId, emojiId, delta, templateAri = '', templateContainerAri = '') =>
	({ setState, getState }) => {
		const state = getState();
		const comment = getCommentState(state, commentId);

		const existing = comment.optimisticReactions.find((r) => r.emojiId === emojiId);

		let nextReactions: ReactionSummary[];

		if (delta === 1) {
			if (existing) {
				// Increment existing entry, preserve `reacted` (current user's state)
				nextReactions = comment.optimisticReactions.map((r) =>
					r.emojiId === emojiId ? { ...r, count: r.count + 1 } : r,
				);
			} else {
				// If this comment has no store entry yet, skip writing: persisting a new
				// CommentReactionState here would create a leaked record (ari: '', containerAri: '')
				// for a comment that is not currently mounted. syncServerReactions will provide
				// authoritative state when the component does mount and refetches.
				if (state.commentReactionState[commentId] === undefined) {
					return;
				}
				// Create a new entry - 'reacted' is false since this is a remote user's reaction
				nextReactions = [
					...comment.optimisticReactions,
					{
						ari: templateAri,
						containerAri: templateContainerAri,
						emojiId,
						count: 1,
						reacted: false,
					},
				];
			}
		} else {
			// delta === -1
			if (!existing) {
				// No emoji found in store - nothing to decrement
				return;
			}
			const newCount = existing.count - 1;
			if (newCount <= 0) {
				nextReactions = comment.optimisticReactions.filter((r) => r.emojiId !== emojiId);
			} else {
				nextReactions = comment.optimisticReactions.map((r) =>
					r.emojiId === emojiId ? { ...r, count: newCount } : r,
				);
			}
		}

		setState({
			commentReactionState: setCommentState(state, commentId, {
				optimisticReactions: nextReactions,
			}),
		});
	};

const actions: Actions = {
	registerOpener,
	triggerOpen,
	openPicker,
	closePicker,
	addOptimisticReaction,
	pruneOptimisticReactions,
	syncServerReactions,
	applyOptimisticToggle,
	applyOptimisticAdd,
	onEmojiSettled,
	onEmojiMutationError,
	cleanupComment,
	applyRemoteDelta,
};

export const store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'inlineReactionStore',
});

export const useInlineReactionActions = createActionsHook(store);

export const useInlineReactionPicker = createStateHook(store, {
	selector: (state: State) => state.picker,
});

export const useOptimisticReactionMarkerMap = createStateHook(store, {
	selector: (state: State) => state.optimisticReactionMarkerMap,
});

/** Read the optimistic marker map once without subscribing to future updates. */
export function getOptimisticReactionMarkerMap(): Record<string, string> {
	return defaultRegistry.getStore(store).storeState.getState().optimisticReactionMarkerMap;
}

/**
 * Returns the optimistic reaction state for a specific comment.
 * Uses a parameterised selector so each component only re-renders when its
 * own comment's state changes, not when any other comment's state updates.
 * Returns a frozen default when the comment has no state in the store yet.
 */
export const useCommentReactionState = createStateHook(store, {
	selector: (state: State, commentId: string): CommentReactionState =>
		state.commentReactionState[commentId] ?? defaultCommentState,
});
